/**
 * Provides the shared dialog shell for the Greenhouse connect flow.
 *
 * GreenhouseSubdomainPanel owns input parsing and actions. GreenhouseOAuthCard is the next file in
 * the flow and either creates an OAuth URL or sends the targeted update.
 */

import React, { FC } from 'react';

import { useTranslation } from '@modernloop/i18n';
import { Dialog } from '@modernloop/shared/components';

import { AtsType } from 'src/generated/mloop-graphql';

import { getAtsService } from 'src/hooks/atsService';

import { GreenhouseSubdomain } from '../GreenhouseSubdomain';

import GreenhouseSubdomainPanel from './GreenhouseSubdomainPanel';

/** Input and lifecycle controls for the Greenhouse OAuth subdomain dialog. */
interface Props {
  initialSubdomain?: string;
  isSubmitting?: boolean;
  onClose: () => void;
  onSubmit: (subdomain: GreenhouseSubdomain) => void;
}

/** Shows the shared Greenhouse subdomain instructions without asking for a V1 API key. */
const GreenhouseOauthSubdomainDialog: FC<Props> = ({
  initialSubdomain = '',
  isSubmitting = false,
  onClose,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const atsService = getAtsService(AtsType.Greenhouse);

  return (
    <Dialog
      open
      onClose={onClose}
      title={t('hiring-dashboard-settings-integrations:settings.integrations.dialogTitle.greenhouse')}
      decoration={
        <img
          style={{ borderRadius: '6px' }}
          height="64px"
          width="64px"
          alt={t('hiring-dashboard-settings-integrations:settings.integrations.apiKeyDialog.logoAlt')}
          src={atsService.logoUrl}
        />
      }
    >
      <GreenhouseSubdomainPanel
        initialSubdomain={initialSubdomain}
        isSubmitting={isSubmitting}
        onCancel={onClose}
        onSubmit={onSubmit}
      />
    </Dialog>
  );
};

export default GreenhouseOauthSubdomainDialog;
